import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import { Drawer } from '@library/components/navigation/Drawer';
import { FormActionButtons } from '@library/components/buttons';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { LeagueGames } from '@library/types/games';
import { RankDrawerBulkRank } from '@bo-components/rankDrawer/RankDrawerBulkRank';
import { RankDrawerGamesSelect } from '@bo-components/rankDrawer/RankDrawerGamesSelect';
import { RankDrawerHeader } from '@bo-components/rankDrawer/RankDrawerHeader';
import { RankDrawerProjections } from '@bo-components/rankDrawer/RankDrawerProjections';
import { RankDrawerStatTypeSelect } from '@bo-components/rankDrawer/RankDrawerStatTypeSelect';
import { RankDrawerTeamsSelect } from '@bo-components/rankDrawer/RankDrawerTeamsSelect';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { useRankDrawer } from '@bo-hooks/useRankDrawer';

interface RankDrawerProps {
  drawerOpen: boolean;
  fromDateString: string;
  initialStatType?: string;
  onCancelDrawer: () => void;
  leagueId: Array<string>;
  leagueInfo?: LeagueInfo;
  statTypes: Array<LeagueProjectionAttributes>;
  games: LeagueGames;
}

export const RankDrawer = ({
  drawerOpen,
  fromDateString,
  initialStatType = undefined,
  onCancelDrawer,
  leagueId,
  leagueInfo = undefined,
  statTypes,
  games
}: RankDrawerProps) => {
  const {
    handleBulkRank,
    isFetching,
    projections,
    resetRank,
    saveDisabled,
    selectedStatType,
    selectedGames,
    selectedTeams,
    saveProjections,
    setProjections,
    handleSelectStatType,
    handleSelectGame,
    handleSelectTeam,
    setStartingRank,
    showDuration,
    startingRank,
    statOptions,
    gameOptions,
    teamOptions
  } = useRankDrawer({
    drawerOpen,
    fromDateString,
    initialStatType,
    leagueId,
    leagueInfo,
    statTypes,
    games
  });

  const handleCancelDrawer = () => {
    resetRank();
    onCancelDrawer();
  };

  return (
    <Drawer
      direction="right"
      isOpen={drawerOpen}
      onCloseDrawer={handleCancelDrawer}
      size="large"
    >
      <div className="bg-soFresh-100 relative grid h-screen w-full grid-rows-[3rem_6.8rem_3rem_4rem_1fr_5rem]">
        <RankDrawerHeader onCancelModal={handleCancelDrawer} />
        <RankDrawerStatTypeSelect
          handleSelectStatType={handleSelectStatType}
          selectedStatType={selectedStatType}
          statOptions={statOptions}
        />
        <div className="flex items-center justify-between px-6 pt-2">
          <div className="flex flex-row gap-2">
            <RankDrawerGamesSelect
              handleSelectGame={handleSelectGame}
              selectedGames={selectedGames}
              gameOptions={gameOptions}
            />

            <RankDrawerTeamsSelect
              handleSelectTeam={handleSelectTeam}
              selectedTeams={selectedTeams}
              teamOptions={teamOptions}
            />
          </div>
          <Tooltip
            className="text-soClean-180"
            position="left"
            text="Sort order: Promotions > Start time > Game Id > Rank"
          >
            <InformationCircleIcon className="text-soClean-180 size-6" />
          </Tooltip>
        </div>
        <RankDrawerBulkRank
          handleBulkRank={handleBulkRank}
          setStartingRank={setStartingRank}
          startingRank={startingRank}
        />
        <RankDrawerProjections
          isFetching={isFetching}
          leagueInfo={leagueInfo}
          projections={projections}
          setProjections={setProjections}
          showDuration={showDuration}
        />
        <FormActionButtons
          className="border-soFresh-130 bg-soFresh-100 border-t pb-6 pt-4"
          primary={{
            onClick: saveProjections,
            disabled: saveDisabled
          }}
          secondary={{
            disabled: saveDisabled,
            label: 'Reset Changes',
            onClick: resetRank
          }}
        />
      </div>
    </Drawer>
  );
};
